import React from 'react';
import PropTypes from 'prop-types';

/**
 * Error boundary component to catch and display React errors gracefully
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-6">
          <h2 className="text-lg font-semibold text-red-200 mb-2">Something went wrong</h2>
          <p className="text-sm text-red-300 mb-4">
            An error occurred while rendering this component. Please refresh the page or contact support if the issue persists.
          </p>
          {this.props.showDetails && this.state.error && (
            <details className="mt-4">
              <summary className="text-xs text-red-400 cursor-pointer mb-2">Error Details</summary>
              <pre className="text-xs text-red-300 bg-red-900/30 p-3 rounded overflow-auto max-h-48">
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
          {this.props.onRetry && (
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                if (this.props.onRetry) {
                  this.props.onRetry();
                }
              }}
              className="mt-4 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-lg text-sm text-red-200 hover:bg-red-500/30 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showDetails: PropTypes.bool,
  onRetry: PropTypes.func,
};

export default ErrorBoundary;

